import { batch, computed, signal } from "@preact/signals";

import readlangClient from "readlangClient";
import UserStore from "stores/UserStore";

interface GuideStoreOptions {
  /** Only `isNew` is read, so tests can pass a minimal stand-in */
  user: Pick<UserStore, "isNew">;
}

export interface GuideGlossaryEntry {
  term: string;
  aliases: string[];
  /**
   * The term's name in the guide's language, "" when it's the same; absent on
   * guides before promptVersion 1.5
   */
  translation?: string;
  /** Free-form category in the guide's language, e.g. "person"; "" for none */
  type: string;
  description: string;
}

export interface GuideSection {
  title: string;
  summary: string;
  fromPosition: number;
  toPosition: number;
}

export interface GuideData {
  /** Spoiler-free pre-reading briefing; absent on guides from promptVersion 1.0 */
  background?: string;
  glossary: GuideGlossaryEntry[];
  sections: GuideSection[];
}

export interface GuideAllowance {
  limit: number;
  used: number;
  chargeWords: number;
  maxWords: number;
  isPremium: boolean;
}

export interface GuideState {
  /**
   * unloaded → loading → none | generating | ready | error;
   * "unavailable" when the user isn't logged in (local mode).
   */
  status:
    | "unloaded"
    | "loading"
    | "unavailable"
    | "none"
    | "generating"
    | "ready"
    | "error";
  bookID?: string;
  /** Language of the guide shown (or offered for generation) */
  language?: string;
  /** Languages the book has a ready or generating guide in */
  languages?: string[];
  /** Whether the ready guide was generated before the text was edited */
  outdated?: boolean;
  progress?: number;
  guide?: GuideData;
  allowance?: GuideAllowance;
  errorMessage?: string;
}

const POLL_INTERVAL_MS = 2500;
const MAX_CONSECUTIVE_POLL_FAILURES = 3;

/**
 * Normalization applied to both glossary terms/aliases and clicked words so
 * that clicking "Quijote," matches the alias "quijote".
 */
const normalizeTerm = (text: string) =>
  text
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(
      /^[\s"'«»“”‘’.,;:!?¡¿()[\]—–-]+|[\s"'«»“”‘’.,;:!?¡¿()[\]—–-]+$/g,
      ""
    );

/**
 * Holds the AI Reading Guide for the book currently open in the reader.
 */
export default class GuideStore {
  constructor(private options: GuideStoreOptions) {}

  state = signal<GuideState>({ status: "unloaded" });

  /** Index into guide.glossary of the entry to highlight in the Guide tab */
  selectedEntryIndex = signal<number | undefined>(undefined);

  /**
   * Bumped on every selection (even of the same entry) so the view re-scrolls
   * to the entry when the user clicks the same term again.
   */
  selectionSequence = signal(0);

  /** Indexes of sections the user has manually revealed ahead of their position */
  revealedSections = signal<Set<number>>(new Set());

  private pollTimer?: ReturnType<typeof setTimeout>;
  private consecutivePollFailures = 0;

  private termToEntryIndex = computed(() => {
    const map = new Map<string, number>();
    const glossary = this.state.value.guide?.glossary ?? [];
    for (const [index, entry] of glossary.entries()) {
      for (const name of [entry.term, ...entry.aliases]) {
        const normalized = normalizeTerm(name);
        if (normalized && !map.has(normalized)) {
          map.set(normalized, index);
        }
      }
    }
    return map;
  });

  /**
   * Each word of a multi-word term/alias, mapped to the names it could
   * complete via its neighbours in the text — so clicking just "Juan" inside
   * "Juan Banús" still finds the entry (see findEntry).
   */
  private wordToMultiWordNames = computed(() => {
    const map = new Map<
      string,
      { entryIndex: number; words: string[]; offset: number }[]
    >();
    const glossary = this.state.value.guide?.glossary ?? [];
    for (const [entryIndex, entry] of glossary.entries()) {
      for (const name of [entry.term, ...entry.aliases]) {
        const words = name.split(/\s+/).map(normalizeTerm).filter(Boolean);
        if (words.length < 2) {
          continue;
        }
        for (const [offset, word] of words.entries()) {
          const candidates = map.get(word) ?? [];
          candidates.push({ entryIndex, words, offset });
          map.set(word, candidates);
        }
      }
    }
    return map;
  });

  /**
   * Loads the guide state for a book, resetting any previous book's guide.
   * Without a language the server picks one the user already has a guide in,
   * preferring their first language.
   */
  async loadForBook(bookID: string, language?: string) {
    this.stopPolling();
    this.selectedEntryIndex.value = undefined;
    this.revealedSections.value = new Set();
    this.state.value = { status: "loading", bookID, language };

    // Anonymous visitors can read public books, but the guide endpoints
    // require login — skip the request so its 401 can't trigger the global
    // logged-out reload path
    if (this.options.user.isNew.value || (await readlangClient.isLocalMode())) {
      this.state.value = { status: "unavailable", bookID };
      return;
    }

    try {
      const response = await readlangClient.request(
        "GET",
        `/api/book/${bookID}/guide`,
        language ? { language } : {}
      );
      if (!this.isShowing(bookID, language)) {
        return; // the user has moved to another book or language meanwhile
      }
      this.applyResponse(bookID, response.data);
    } catch (_error) {
      if (this.isShowing(bookID, language)) {
        this.state.value = { status: "unavailable", bookID };
      }
    }
  }

  /**
   * Starts generation for the current book in the current guide language,
   * replacing an outdated guide. Errors (allowance exceeded, text too long)
   * surface in state.errorMessage while returning to status "none" (the
   * intro screen), or "ready" when replacing an outdated guide.
   */
  async generate() {
    const previousState = this.state.value;
    const { bookID, language } = previousState;
    if (!bookID || !language || this.options.user.isNew.value) {
      return;
    }

    this.state.value = {
      ...previousState,
      status: "generating",
      progress: 0,
      errorMessage: undefined,
    };

    try {
      await readlangClient.request("POST", `/api/book/${bookID}/guide`, {
        language,
      });
      if (this.isShowing(bookID, language)) {
        this.consecutivePollFailures = 0;
        this.schedulePoll(bookID, language);
      }
    } catch (error: any) {
      if (!this.isShowing(bookID, language)) {
        return;
      }
      const serverError = error?.jqXHR?.data?.error;
      this.state.value = {
        // A refused replacement leaves the outdated guide in place
        ...previousState,
        status: previousState.status === "ready" ? "ready" : "none",
        // On a terminal failure (network loss, 5xx) readlangClient already
        // notified, so skip our own message and just restore the intro screen.
        errorMessage: error?.jqXHR?.alreadyNotified
          ? undefined
          : serverError === "allowanceExceeded"
            ? "You don't have enough Reading Guide allowance left this month for this text."
            : serverError ??
              "Couldn't start generating the guide. Please try again.",
      };
    }
  }

  /**
   * Looks up a clicked word or selected phrase in the glossary. When the
   * clicked text alone doesn't match, `neighborWord` (text of the word
   * `offset` positions away in the book, if any) lets a single click on one
   * word of a multi-word name like "Juan Banús" still find the entry —
   * checking the actual neighbours also disambiguates shared first names.
   *
   * @returns the glossary entry index, or undefined if there's no match
   */
  findEntry(
    text: string,
    neighborWord?: (offset: number) => string | undefined
  ): number | undefined {
    if (this.state.value.status !== "ready") {
      return undefined;
    }
    const normalized = normalizeTerm(text);
    const exactMatch = this.termToEntryIndex.value.get(normalized);
    if (exactMatch !== undefined || !neighborWord) {
      return exactMatch;
    }
    for (const {
      entryIndex,
      words,
      offset,
    } of this.wordToMultiWordNames.value.get(normalized) ?? []) {
      const surroundingWordsMatch = words.every(
        (word, index) =>
          index === offset ||
          normalizeTerm(neighborWord(index - offset) ?? "") === word
      );
      if (surroundingWordsMatch) {
        return entryIndex;
      }
    }
    return undefined;
  }

  selectEntry(index: number) {
    // Batched so the view's selection effect runs once, not once per signal
    batch(() => {
      this.selectedEntryIndex.value = index;
      this.selectionSequence.value++;
    });
  }

  revealSection(index: number) {
    const revealed = new Set(this.revealedSections.value);
    revealed.add(index);
    this.revealedSections.value = revealed;
  }

  /**
   * Set when another page (e.g. the review-summary page's "full plot summary"
   * link) asks the reader to open the Guide tab on its next load; consumed
   * once by the reader page.
   */
  private openTabRequested = false;

  requestOpenTab() {
    this.openTabRequested = true;
  }

  consumeOpenTabRequest() {
    const requested = this.openTabRequested;
    this.openTabRequested = false;
    return requested;
  }

  /**
   * Whether a plot-summary section can be shown without spoilers. Each
   * summary covers the section's whole range, so it only unlocks once the
   * reader has read to the section's end (reaching its start isn't enough —
   * that would reveal events ahead of the current position), unless the user
   * has manually revealed it.
   */
  isSectionRevealed(section: GuideSection, index: number, readTo: number) {
    return (
      section.toPosition <= readTo || this.revealedSections.value.has(index)
    );
  }

  stopPolling() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = undefined;
    }
  }

  /** Whether the state still belongs to this book and guide language */
  private isShowing(bookID: string, language: string | undefined) {
    return (
      this.state.value.bookID === bookID &&
      this.state.value.language === language
    );
  }

  private applyResponse(bookID: string, response: any) {
    this.state.value = {
      status: response.status,
      bookID,
      language: response.language,
      languages: response.languages,
      outdated: response.outdated,
      progress: response.progress,
      guide: response.guide,
      allowance: response.allowance,
      errorMessage: response.error,
    };
    if (response.status === "generating") {
      // A fresh poll cycle shouldn't inherit failures from a previous book
      this.consecutivePollFailures = 0;
      this.schedulePoll(bookID, response.language);
    }
  }

  private schedulePoll(bookID: string, language: string) {
    this.stopPolling();
    this.pollTimer = setTimeout(async () => {
      this.pollTimer = undefined;
      if (!this.isShowing(bookID, language)) {
        return;
      }
      try {
        const response = await readlangClient.request(
          "GET",
          `/api/book/${bookID}/guide`,
          { language }
        );
        if (!this.isShowing(bookID, language)) {
          return;
        }
        this.consecutivePollFailures = 0;
        this.applyResponse(bookID, response.data);
      } catch (error: any) {
        if (!this.isShowing(bookID, language)) {
          return;
        }
        if (error?.jqXHR?.alreadyNotified) {
          // Terminal failure (network loss, 5xx): readlangClient already
          // notified, so stop polling and restore the intro screen rather
          // than retrying forever behind a stuck "generating" spinner.
          this.state.value = {
            ...this.state.value,
            status: "none",
            errorMessage: undefined,
          };
          return;
        }
        this.consecutivePollFailures++;
        if (this.consecutivePollFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
          // A persistently failing endpoint (e.g. a 404) would otherwise be
          // polled forever behind a stuck "generating" spinner
          this.state.value = {
            ...this.state.value,
            status: "none",
            errorMessage: undefined,
          };
          return;
        }
        this.schedulePoll(bookID, language);
      }
    }, POLL_INTERVAL_MS);
  }
}

export const _functionsForUnitTests = { normalizeTerm };
